import { Link } from "react-router-dom";
import { ArrowRight, Layers } from "lucide-react";
import SEO from "@/components/site/SEO";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { FABRIC_LINES, FABRIC_LINE_ORDER, IMG } from "@/data/site";

export default function CustomCovers() {
  const lines = FABRIC_LINE_ORDER.map((id) => FABRIC_LINES[id]);

  return (
    <div data-testid="custom-covers-page">
      <SEO title="Custom Covers with Our Fabrics" description="Choose from Tanonshop's in-house fabrics — Cowhide-Print Suede, Linen, Duck Canvas and Faux Leather — or bring your own material. Pick a fabric to start your made-to-measure cover." />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-16 pb-12 lg:pt-24">
        <Reveal>
          <Eyebrow>Custom covers with our fabrics</Eyebrow>
          <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.03] max-w-3xl">
            Pick your fabric to begin
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-teal-anchor/70 leading-relaxed font-light">
            Every cover is made to your measurements and reviewed by hand. Choose one of our in-house fabrics
            below to pick colors and see your preview — or bring your own material and we'll take it from there.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-24 lg:pb-32">
        <div className="grid gap-8 md:grid-cols-2">
          {lines.map((f, i) => (
            <Reveal key={f.id} delay={(i % 2) * 0.08}>
              <Link to={`/covers/${f.id}`} data-testid={`cover-line-${f.id}`} className="group flex h-full overflow-hidden rounded-sm border border-teal-anchor/12 bg-white">
                <div className="w-2/5 shrink-0 overflow-hidden bg-teal-anchor/5">
                  <img src={f.hero} alt={f.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h2 className="font-serif text-2xl tracking-tight">{f.name}</h2>
                  <p className="mt-2 flex-1 text-sm text-teal-anchor/70 leading-relaxed">{f.tagline}</p>
                  <div className="mt-4 flex items-center gap-1.5">
                    {f.colors.slice(0, 6).map((c) => (
                      <span key={c.id} className="h-5 w-5 rounded-full border border-teal-anchor/15" style={c.swatch ? { backgroundImage: `url(${c.swatch})`, backgroundSize: "cover" } : { backgroundColor: c.color }} title={c.label} />
                    ))}
                    <span className="ml-2 text-xs text-teal-anchor/50">{f.colors.length} options</span>
                  </div>
                  <span className="mt-5 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-teal-primary">
                    Start with {f.short} <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}

          {/* Customer Own Material */}
          <Reveal delay={0.08} className="md:col-span-2">
            <div className="grid items-center gap-6 overflow-hidden rounded-sm border border-teal-anchor/12 bg-teal-anchor text-paper sm:grid-cols-2">
              <div className="p-8 lg:p-10">
                <Layers size={26} strokeWidth={1.5} className="text-paper/80" />
                <h2 className="mt-4 font-serif text-2xl lg:text-3xl tracking-tight">Customer's Own Material (COM)</h2>
                <p className="mt-3 text-paper/70 leading-relaxed">
                  Prefer to use your own fabric? Ship it to us, or send an item number and store link and we'll
                  help you order it. Head to the Custom Order form with your measurements and details.
                </p>
                <Link to="/custom-order" data-testid="cover-line-com" className="mt-7 inline-flex items-center gap-2 rounded-sm bg-paper px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.15em] text-teal-anchor transition-all duration-200 hover:-translate-y-px">
                  Go to Custom Order <ArrowRight size={15} />
                </Link>
              </div>
              <div className="hidden h-full sm:block">
                <img src={IMG.tools} alt="Customer's own material" className="h-full w-full object-cover" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
